import React, { useState, useEffect, useCallback } from 'react';
import { staffAPI } from '../../services/api';
import DataTable from '../../components/common/DataTable';
import SearchBar from '../../components/common/SearchBar';
import Modal from '../../components/common/Modal';
import toast from 'react-hot-toast';
import { getInitials } from '../../utils/helpers';

/* ─────────────────────────────────────────
   Field helper
───────────────────────────────────────── */
const Field = ({ label, name, type = 'text', value, onChange, required, options, col = 6, rows }) => (
  <div className={`col-md-${col} mb-3`}>
    <label className="form-label">
      {label}{required && <span className="text-danger ms-1">*</span>}
    </label>
    {options ? (
      <select className="form-select" name={name} value={value ?? ''} onChange={onChange} required={required}>
        <option value="">— Select —</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : rows ? (
      <textarea className="form-control" name={name} value={value ?? ''} onChange={onChange} rows={rows} />
    ) : (
      <input type={type} className="form-control" name={name} value={value ?? ''} onChange={onChange} required={required} />
    )}
  </div>
);

/* ─────────────────────────────────────────
   Department Card (grid view)
───────────────────────────────────────── */
const DEPT_COLORS = [
  ['#eff6ff', '#1d4ed8'],
  ['#f0fdf4', '#15803d'],
  ['#faf5ff', '#7c3aed'],
  ['#fff7ed', '#c2410c'],
  ['#f0fdfa', '#0f766e'],
  ['#fef2f2', '#b91c1c'],
  ['#fefce8', '#a16207'],
  ['#f0f9ff', '#0369a1'],
];

const DeptCard = ({ dept, index, staffCount, headName, onEdit, onDelete, onViewStaff }) => {
  const [bg, color] = DEPT_COLORS[index % DEPT_COLORS.length];
  return (
    <div
      style={{
        background: '#fff', borderRadius: 14, boxShadow: '0 1px 8px rgba(0,0,0,0.07)',
        overflow: 'hidden', transition: 'transform 0.2s, box-shadow 0.2s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.07)'; }}
    >
      {/* Colored top stripe */}
      <div style={{ height: 6, background: color }} />

      <div style={{ padding: '20px 20px 16px' }}>
        {/* Icon + Name */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, flexShrink: 0,
            background: bg, color, fontWeight: 800, fontSize: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            🏢
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h6 style={{ margin: 0, fontWeight: 800, fontSize: 15, color: '#0f172a', lineHeight: 1.3 }}>
              {dept.name}
            </h6>
            {dept.description && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.5,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {dept.description}
              </p>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <div style={{ flex: 1, background: bg, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1 }}>{staffCount}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>Staff Members</div>
          </div>
          <div style={{ flex: 1, background: '#f8fafc', borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1a3a5c', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {headName || '—'}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>Department Head</div>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-sm"
            style={{ flex: 1, background: bg, color, border: 'none', fontWeight: 600, fontSize: 12 }}
            onClick={() => onViewStaff(dept)}
          >
            👥 View Staff
          </button>
          <button className="btn btn-sm btn-outline-primary" onClick={() => onEdit(dept)}>✏️</button>
          <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(dept.id)}>🗑️</button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   Staff in Department side-panel
───────────────────────────────────────── */
const DeptStaffPanel = ({ dept, allStaff, onClose }) => {
  const members = allStaff.filter(s => s.department === dept?.id);

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 360,
      background: '#fff', boxShadow: '-8px 0 32px rgba(0,0,0,0.15)',
      zIndex: 1050, display: 'flex', flexDirection: 'column',
      transform: dept ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s ease',
    }}>
      {/* Header */}
      <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #f1f5f9', background: '#0f172a' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h5 style={{ margin: 0, color: '#fff', fontWeight: 800 }}>{dept?.name}</h5>
            <small style={{ color: 'rgba(255,255,255,0.5)' }}>{members.length} staff members</small>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16 }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Staff list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {members.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>👤</div>
            <p>No staff assigned to this department yet.</p>
          </div>
        ) : (
          members.map(s => (
            <div key={s.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 14px', borderRadius: 12, marginBottom: 8,
              background: '#f8fafc', transition: 'background 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
            >
              <div style={{
                width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg,#1a3a5c,#0ea5e9)',
                color: '#fff', fontWeight: 700, fontSize: 13,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {getInitials(s.full_name || `${s.first_name} ${s.last_name}`)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                  {s.full_name || `${s.first_name} ${s.last_name}`}
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{s.designation}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.employee_id}</div>
              </div>
              <span style={{
                background: s.status === 'active' ? '#d1fae5' : '#fee2e2',
                color: s.status === 'active' ? '#065f46' : '#991b1b',
                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
              }}>
                {s.status}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {members.length > 0 && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9' }}>
          <a href="/staff" className="btn btn-primary w-100 btn-sm">
            👨‍💼 Manage All Staff →
          </a>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   ROOT EXPORT — DepartmentsManagement
═══════════════════════════════════════════ */
const DepartmentsManagement = () => {
  const [departments, setDepartments] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [panelDept, setPanelDept] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', head: '' });

  const loadDepts = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      staffAPI.departments(),
      staffAPI.list({ page_size: 200, status: 'active' }),
    ]).then(([dRes, sRes]) => {
      if (dRes.status === 'fulfilled') setDepartments(dRes.value.data.results || dRes.value.data);
      if (sRes.status === 'fulfilled') setAllStaff(sRes.value.data.results || sRes.value.data);
    }).catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadDepts(); }, [loadDepts]);

  const filtered = departments.filter(d =>
    d.name.toLowerCase().includes(search.toLowerCase()) ||
    (d.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const getStaffCount = (deptId) => allStaff.filter(s => s.department === deptId).length;
  const getHeadName = (headId) => {
    const s = allStaff.find(s => s.id === headId);
    return s ? (s.full_name || `${s.first_name} ${s.last_name}`) : null;
  };

  const openAdd = () => { setEditItem(null); setForm({ name: '', description: '', head: '' }); setShowModal(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ name: item.name, description: item.description || '', head: item.head || '' });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, head: form.head || null };
      if (editItem) {
        await staffAPI.updateDepartment(editItem.id, payload);
        toast.success('Department updated!');
      } else {
        await staffAPI.createDepartment(payload);
        toast.success('Department created!');
      }
      setShowModal(false);
      loadDepts();
    } catch (err) {
      const errors = err.response?.data;
      if (errors) Object.entries(errors).forEach(([k, v]) => toast.error(`${k}: ${Array.isArray(v) ? v[0] : v}`));
      else toast.error('Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this department? Staff assigned to it will lose their department.')) return;
    try {
      await staffAPI.deleteDepartment(id);
      toast.success('Department deleted');
      if (panelDept?.id === id) setPanelDept(null);
      loadDepts();
    } catch { toast.error('Cannot delete — check for linked records'); }
  };

  // Table columns (for table view)
  const tableColumns = [
    {
      key: 'name', label: 'Department',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🏢</div>
          <div>
            <strong>{v}</strong>
            {row.description && <div style={{ fontSize: 12, color: '#64748b' }}>{row.description.slice(0, 50)}{row.description.length > 50 ? '…' : ''}</div>}
          </div>
        </div>
      ),
    },
    {
      key: 'id', label: 'Staff Count',
      render: (id) => {
        const cnt = getStaffCount(id);
        return (
          <span style={{ background: cnt > 0 ? '#eff6ff' : '#f1f5f9', color: cnt > 0 ? '#1d4ed8' : '#94a3b8', padding: '4px 12px', borderRadius: 20, fontWeight: 700, fontSize: 13 }}>
            👤 {cnt}
          </span>
        );
      },
    },
    {
      key: 'head', label: 'Head of Department',
      render: (v) => {
        const name = getHeadName(v);
        return name
          ? <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#1a3a5c,#0ea5e9)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{getInitials(name)}</div>
              <span style={{ fontSize: 13 }}>{name}</span>
            </div>
          : <span style={{ color: '#94a3b8', fontSize: 13 }}>— Not assigned</span>;
      },
    },
    {
      key: 'id', label: 'Actions',
      render: (id, row) => (
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-info" onClick={() => setPanelDept(row)}>👥 Staff</button>
          <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(row)}>✏️ Edit</button>
          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(id)}>🗑️</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h2>Departments Management</h2>
          <p>Organise staff into departments and assign department heads</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>➕ Add Department</button>
      </div>

      {/* Summary Row */}
      <div className="row g-3 mb-4">
        {[
          { icon: '🏢', label: 'Total Departments', value: departments.length, bg: '#eff6ff', color: '#1d4ed8' },
          { icon: '👤', label: 'Total Staff', value: allStaff.length, bg: '#f0fdf4', color: '#15803d' },
          { icon: '👔', label: 'Heads Assigned', value: departments.filter(d => d.head).length, bg: '#faf5ff', color: '#7c3aed' },
          { icon: '⚠️', label: 'Unassigned Heads', value: departments.filter(d => !d.head).length, bg: '#fff7ed', color: '#c2410c' },
        ].map(s => (
          <div key={s.label} className="col-6 col-lg-3">
            <div style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
              <div style={{ width: 50, height: 50, borderRadius: 13, background: s.bg, color: s.color, fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{s.icon}</div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-center">
            <div className="col-md-6">
              <SearchBar onSearch={setSearch} placeholder="Search departments by name or description…" />
            </div>
            <div className="col-md-6 d-flex justify-content-end gap-2">
              <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 3 }}>
                {[['grid', '⊞ Grid'], ['table', '☰ Table']].map(([k, l]) => (
                  <button key={k} onClick={() => setViewMode(k)}
                    style={{
                      border: 'none', cursor: 'pointer', padding: '6px 14px',
                      borderRadius: 6, fontWeight: 600, fontSize: 12, transition: 'all 0.2s',
                      background: viewMode === k ? '#fff' : 'transparent',
                      color: viewMode === k ? '#1a3a5c' : '#64748b',
                      boxShadow: viewMode === k ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                    }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-spinner">
          <div className="spinner-border text-primary" role="status" />
          <span className="ms-3">Loading departments…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏢</div>
          <h5>No Departments Found</h5>
          <p>{search ? 'Try a different search term.' : 'Get started by adding your first department.'}</p>
          {!search && <button className="btn btn-primary mt-2" onClick={openAdd}>➕ Add Department</button>}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="row g-3">
          {filtered.map((dept, i) => (
            <div key={dept.id} className="col-md-6 col-lg-4">
              <DeptCard
                dept={dept}
                index={i}
                staffCount={getStaffCount(dept.id)}
                headName={getHeadName(dept.head)}
                onEdit={openEdit}
                onDelete={handleDelete}
                onViewStaff={(d) => setPanelDept(d)}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="card-header"><span>🏢 Departments ({filtered.length})</span></div>
          <DataTable columns={tableColumns} data={filtered} loading={false} />
        </div>
      )}

      {/* Staff side panel */}
      {panelDept && (
        <>
          <div
            onClick={() => setPanelDept(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1049 }}
          />
          <DeptStaffPanel dept={panelDept} allStaff={allStaff} onClose={() => setPanelDept(null)} />
        </>
      )}

      {/* Add / Edit Modal */}
      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={editItem ? `Edit Department — ${editItem.name}` : 'Add New Department'}
        size="lg"
        footer={
          <div className="d-flex gap-2 justify-content-end w-100">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={handleSave}>
              {saving
                ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
                : editItem ? '💾 Update Department' : '➕ Create Department'}
            </button>
          </div>
        }
      >
        <form onSubmit={handleSave}>
          <div className="row">
            <Field label="Department Name" name="name" value={form.name} onChange={handleChange} required col={12} />
            <Field label="Description" name="description" value={form.description} onChange={handleChange} col={12} rows={3} />
            <Field
              label="Head of Department"
              name="head"
              value={form.head}
              onChange={handleChange}
              col={12}
              options={allStaff.map(s => ({
                value: s.id,
                label: `${s.full_name || `${s.first_name} ${s.last_name}`} (${s.designation})`,
              }))}
            />
          </div>
          {allStaff.length === 0 && (
            <div className="alert alert-warning" style={{ fontSize: 13 }}>
              ⚠️ No active staff found. <a href="/staff">Add staff first</a> to assign a department head.
            </div>
          )}
        </form>
      </Modal>
    </div>
  );
};

export default DepartmentsManagement;
